import React, {useState, useCallback, useEffect, createContext, useContext} from 'react';
import Stack from '@mui/material/Stack';
import {MyTypography} from '../../common/components';
import MyButton from '../../common/components/MyButton';
import OcrAnnotation from '../OcrAnnotation';
import Fuse from 'fuse.js';

const ProviderDropdownDialogContext = createContext();

export function ProviderDropdownDialogProvider({ children }) {
    const [dialogParams, setDialogParams] = useState({open: false, defaultName: '', timestamp: null});

    const openDialog = useCallback((name) => {
        setDialogParams(prevState => ({...prevState, open: true, timestamp: Date.now(), defaultName: name}));
    }, []);

    useEffect(() => {
        return () => {
            setDialogParams(prevState => ({...prevState, open: false, defaultName: ''}));
        };
    }, []);

    return (
        <ProviderDropdownDialogContext.Provider value={{ openDialog, ...dialogParams }}>
            {children}
        </ProviderDropdownDialogContext.Provider>
    );
}

export function useProviderDropdownDialog() {
    return useContext(ProviderDropdownDialogContext);
}


export function useOcrProviderForm({setValue, inputName=null}) {
    const parsedInputName = inputName?.replace('person:', '');

    console.log('useOcrProviderForm:inputName=',inputName,' parsedInputName=',parsedInputName);

    const [options, setOptions] = useState([]);
    const { openDialog: openAddProviderDialog } = useProviderDropdownDialog();

    const [ocrDetails, setOcrDetails] = useState({
        inputName: parsedInputName,
        ocrSuggestionExistOnDropdown: false,
        referringProviderMatch: null,
        note: null,
    });

    const onGetMatchingItem = useCallback((referringProviderName) => {
        if (!referringProviderName || !options || options.length === 0) {
            return {id: null, name: referringProviderName};
        }

        // Fuse.js options (you can tweak threshold for fuzziness)
        const fuse = new Fuse(options, {
            keys: ['name'],
            includeScore: true,
            threshold: 0.3, // lower = stricter match
        });

        const result = fuse.search(referringProviderName);

        if (result.length > 0) {
            // Take the best match
            const bestMatch = result[0];
            // we return the id
            return {
                id: bestMatch.item.id,
                name: bestMatch.item.name,
            };
        }

        // otherwise we just return the name
        return {id: null, name: referringProviderName};
    }, [options]);

    const triggerSelect = useCallback((value) => () => {

        const match = onGetMatchingItem(value);

        // toggle add dialog
        if (match.id) {
            setValue('referring_provider', match.id);
        } else {
            openAddProviderDialog(match.name);
        }
    }, [setValue, onGetMatchingItem, openAddProviderDialog]);

    const onOptionsLoaded = useCallback((data) => {
        setOptions(data);
    }, []);

    useEffect(() => {
        const match = onGetMatchingItem(parsedInputName);

        if (match.id) {
            setOcrDetails({
                inputName: parsedInputName,
                ocrSuggestionExistOnDropdown: true,
                referringProviderMatch: match.id,
                note: `We found '${match.name}' as a possible match for '${parsedInputName}'`,
            });
        } else {
            setOcrDetails({
                inputName: parsedInputName,
                ocrSuggestionExistOnDropdown: false,
                referringProviderMatch: match.name,
                note: `We can't find '${parsedInputName}' on the Provider list. You can create this provider by clicking the first option on the Provider dropdown.`,
            });
        }

        return () => {
            setOcrDetails({
                inputName: null,
                ocrSuggestionExistOnDropdown: false,
                referringProviderMatch: null,
                note: null,
            });
        };
    }, [parsedInputName, onGetMatchingItem, triggerSelect]);

    return {
        onOptionsLoaded,
        triggerSelect,
        ocrDetails,
    };
}

export default function ReferringProviderOcrAnnotation({triggerSelect, ocrDetails, title="AI Detected Provider", severity="error"}) {
    return (
        <OcrAnnotation title={title}
                       severity={severity}>
            <Stack direction="column"
                   spacing={1}>
                <MyTypography variant="caption" sx={{fontWeight: 600, color: 'inherit'}}>{ocrDetails.inputName}</MyTypography>

                <MyButton variant="contained"
                          size="small"
                          color={severity}
                          onClick={triggerSelect}
                          fullWidth
                          disableElevation>USE THIS PROVIDER</MyButton>
                {
                    ocrDetails.note &&
                    <MyTypography variant="caption" sx={{color: 'inherit'}}>Note: {ocrDetails.note}</MyTypography>
                }
            </Stack>
        </OcrAnnotation>
    );
}